import angular from 'angular'
import isEmpty from 'lodash/isEmpty'
import isNaN from 'lodash/isNaN'
import { vn } from 'vend-number'

const isDefined = angular.isDefined
const isUndefined = angular.isUndefined

/**
 * Formats a form field for accepting VendNumber values
 *
 * @module vend.js.vdNumber
 */
angular.module('vend.ui').directive('vdNumber', function ($filter) {
  return {
    require: 'ngModel',
    restrict: 'A',
    scope: {
      /**
       * Optional object of settings for vend-number directive that affects end value.
       *
       * vdNumberOptions {Object}
       *
       *      [min] {Number}
       *            The minimum the end value should be to be valid
       *
       *      [exclusiveMinimum=false] {Boolean}
       *            Whether to only allow values bigger than min or allow values equal to min as well.
       *
       *      [max] {Number}
       *            The maximum the end value should be to be valid
       *
       *      [exclusiveMaximum=false] {Boolean}
       *            Whether to only allow values less than max or allow values equal to max as well.
       *
       *      [decimalPlaces=2] {Number}
       *            Number of decimal places to pad the end value to
       *
       *      [trimInsignificantDigits=false] {Boolean}
       *            True when insignificant digits should be trimmed in end value
       *
       *      [allowedSuffix] {String}
       *            A string that the given value may have as suffix that can be ignored / treated as valid.
       *
       *      [allowedPrefix] {String}
       *            A string that the given value may have as prefix that can be ignored / treated as valid.
       *
       *      [allowEmpty=false] {Boolean}
       *            Whether to allow empty (""/undefined/null) values. Defaults to false which will default
       *            empty values to "0"
       *
       *      [updateOnChange=false] {Boolean}
       *            Whether to allow the $formatter (formatVendNumber) to update the view value when the model is
       *            updated and the text input is focused. By default this only happens on blur.
       */
      vdNumberOptions: '=',
      /**
       * Optional error fallback value, this will be rendered in the input when the form input is invalid
       * and therefore set as undefined on the model
       */
      vdNumberErrorFallback: '=',
    },

    /**
     * =======================================
     * VEND NUMBER LINK
     * =======================================
     */
    link(scope, $element, attrs, ngModelCtrl) {
      const displayRoundFilterFn = $filter('vdDisplayRound')
      let options = scope.vdNumberOptions || {}

      /**
       * Watch changes to the options and re-validate the model if any option changes
       */
      scope.$watch(
        'vdNumberOptions',
        function (vdNumberOptions) {
          options = vdNumberOptions || {}

          // Update validation - rules may have change with new options
          ngModelCtrl.$validate()

          if (isDefined(ngModelCtrl.$modelValue)) {
            // View value to be set with new rules
            setViewValue()
          }
        },
        true
      )

      /**
       * Model -> View: Format given model input value and return the correct display value taking configured
       * options into account, e.g. decimalPlaces & trimInsignificant digits settings.
       *
       * @private
       * @method formatVendNumber
       *
       * @param input {VendNumber}
       *        A vend number input value to format for display.
       *
       * @return {String} The given vend number input formatted for display.
       */
      function formatVendNumber(input) {
        const isElementFocussed = $element[0].matches(':focus')
        let formattedNumber

        if (input !== undefined && input !== null) {
          formattedNumber = String(input)
        } else {
          formattedNumber = input
        }

        // Bypassed if `updateOnChange` is true, allowing the formatter to update the view value to match the model
        if (isElementFocussed && !options.updateOnChange) {
          // If element is currently focused, don't format for display until blur, & just return current
          // element input value to prevent *any* changes while the user (or Cypress test) is typing.
          return $element.val()
        }

        if (!isEmpty(formattedNumber)) {
          formattedNumber = displayRoundFilterFn(
            formattedNumber,
            options.decimalPlaces,
            options.trimInsignificantDigits
          )
        }

        // If the formattedNumber is undefined and a fallback value is available render that in the input
        // Example: this could be used for in field user feedback for invalid fields
        if (
          !isDefined(formattedNumber) &&
          isDefined(scope.vdNumberErrorFallback)
        ) {
          formattedNumber = scope.vdNumberErrorFallback

          // we set the form as dirty so the styles for invalid forms are applied
          ngModelCtrl.$setDirty()
        }

        return formattedNumber
      }

      /**
       * View -> Model: Parse given display input value (remove any allowed characters) and return the correct
       * model value.
       *
       * There are special cases implemented for when the input is currently focused (i.e. we can assume from
       * this that the user is still typing) whereby certain usually invalid cases are interpreted as 0 for
       * example to ensure that calculations and validity do not break prematurely to a valid number being finished
       * (e.g. in cases of slow typing and such).
       *
       * @private
       * @method parseVendNumber
       *
       * @param input {VendNumber}
       *        A vend number input value to parse and round for display.
       *
       * @return {String} The given vend number input parsed for model.
       */
      function parseVendNumber(input) {
        const allowNegative =
          !angular.isDefined(options.min) || Number(options.min) < 0
        const { allowedPrefix, allowEmpty } = options
        let parsedNumber = input

        // Trim any leading / trailing spaces
        parsedNumber = parsedNumber.trim()

        // Special case to handle empty input as valid while typing when empty is not allowed.
        if (!allowEmpty && isEmpty(parsedNumber)) {
          parsedNumber = '0'
        }
        // When empty is allowed the empty string will be returned validly no matter if focused or not.

        // Special case to handle the input of allowedPrefix alone as valid while typing
        if (allowedPrefix && parsedNumber === allowedPrefix) {
          parsedNumber = '0'
        }

        if (options.allowedPrefix) {
          parsedNumber = String(parsedNumber).replace(allowedPrefix, '')
        }

        // @note allowedSuffix alone is invalid (i.e. because you do not type suffix first)
        if (options.allowedSuffix && parsedNumber.length > 1) {
          parsedNumber = String(parsedNumber).replace(options.allowedSuffix, '')
        }

        // Remove all characters except for 0-9, - and .
        if (options.stripNonNumeric) {
          parsedNumber = String(parsedNumber).replace(/[^\d.-]/g, '')
        }

        // Special case to handle the input of "-" or "." alone as valid while typing (this is specifically
        // handled after already parsing prefix / suffixes due to cases like "-$50")
        if ((allowNegative && parsedNumber === '-') || parsedNumber === '.') {
          parsedNumber = '0'
        }

        // If the total length of the string after trimming is zero, there is no true value present (only spaces), so
        // set to zero unless allowEmpty is true.
        if (!allowEmpty && String(parsedNumber).trim().length === 0) {
          parsedNumber = '0'
        }

        // Store last parsed number
        $element.data('vdNumberLastParsed', parsedNumber)

        return parsedNumber
      }

      /**
       * Model -> View: Set view value to current model value rounded for display and set as current input
       * element value.
       *
       * @private
       * @method setViewValue
       */
      function setViewValue() {
        // Leave invalid values intact so that user can see the error and fix it.
        if (ngModelCtrl.$invalid) {
          return
        }
        // Format the display values decimal places (so as not to interrupt user typing)
        // @note We format the input element value explicitly because in some cases ngModelCtrl.$modelValue
        // has not yet been updated with the latest user input (e.g. in Cypress tests that are super fast).
        scope.$applyAsync(function () {
          const parsedInputValue = parseVendNumber($element.val())
          let viewValue

          // Format the input value unless the value is empty and we allow empty input.
          if (!options.allowEmpty || !isEmpty(parsedInputValue)) {
            viewValue = displayRoundFilterFn(
              parsedInputValue,
              options.decimalPlaces,
              options.trimInsignificantDigits,
              options.truncateLimit
            )
          } else {
            viewValue = parsedInputValue
          }

          ngModelCtrl.$setViewValue(viewValue)
          ngModelCtrl.$render()
        })
      }

      /**
       * Re-run display method on blur, as there is different behavior when element is focused, i.e. to account
       * for the user typing.
       */
      $element.on('blur', function () {
        setViewValue()
      })

      /**
       * Ensure the value is a valid number. This is instead of setting the input to be a number, because this
       * doesn't allow us to enforce decimal places.
       *
       * @method validCharacters
       *
       * @param value {VendNumber}
       *        The value to validate (has been parsed).
       *
       * @return {Boolean} Returns true if given value is a valid number, otherwise false.
       */
      ngModelCtrl.$validators.validCharacters = function (value) {
        let validCharacters = true

        if (!options.allowEmpty && isEmpty(value)) {
          validCharacters = false
        }

        validCharacters = !isNaN(Number(value))

        return validCharacters
      }

      /**
       * Return true when given value is above configured min.
       *
       * @method validMinValue
       *
       * @param value {VendNumber}
       *        The value to validate.
       *
       * @return {Boolean} Returns true when given value is above min, otherwise false.
       */
      ngModelCtrl.$validators.validMinValue = function (value) {
        if (isUndefined(options.min)) {
          return true
        }

        if (isNaN(Number(value))) {
          return false
        }

        if (options.exclusiveMinimum) {
          return vn(value).gt(options.min)
        }

        return vn(value).gte(options.min)
      }

      /**
       * Return true when given value is below configured max.
       *
       * @method validMaxValue
       *
       * @param value {VendNumber}
       *        The value to validate.
       *
       * @return {Boolean} Returns true when given value is below max, otherwise false.
       */
      ngModelCtrl.$validators.validMaxValue = function (value) {
        if (isUndefined(options.max)) {
          return true
        }

        if (isNaN(Number(value))) {
          return false
        }

        if (options.exclusiveMaximum) {
          return vn(value).lt(options.max)
        }

        return vn(value).lte(options.max)
      }

      // Round values changed on model for display (model to view)
      ngModelCtrl.$formatters.push(formatVendNumber)

      // Round values changed by user for model (view to model)
      ngModelCtrl.$parsers.push(parseVendNumber)
    },
  }
})
